/**
 * RFQ service for handling RFQ-related operations
 */

import axiosClient from "../../config/axiosClient";
import { ENDPOINTS } from "../../config/endpoints";
import {
  Rfq,
  RfqDetail,
  RfqQuote,
  CreateRfqPayload,
  UpdateRfqPayload,
  CreateQuotePayload,
  UpdateQuotePayload,
  RfqListParams,
  PaginationParams,
  RfqResponse,
  RfqListResponse,
  RfqDetailResponse,
  QuoteResponse,
  QuoteListResponse,
} from "./rfq.types";

class RfqService {
  /**
   * Create a new RFQ
   */
  async createRfq(data: CreateRfqPayload): Promise<RfqResponse> {
    const response = await axiosClient.post<{
      success: boolean;
      data: RfqResponse;
    }>(ENDPOINTS.RFQ.BASE, data);
    // Handle double-wrapped response: { success: true, data: { success: true, data: {...} } }
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      // Check if inner data is also wrapped
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      // Otherwise return the inner data directly
      return innerData as RfqResponse;
    }
    // Fallback: if structure is different, return the data directly
    return response.data as any;
  }

  /**
   * Update an existing RFQ
   */
  async updateRfq(id: string, data: UpdateRfqPayload): Promise<RfqResponse> {
    const response = await axiosClient.patch<{
      success: boolean;
      data: RfqResponse;
    }>(ENDPOINTS.RFQ.byId(id), data);
    // Handle double-wrapped response: { success: true, data: { success: true, data: {...} } }
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      // Check if inner data is also wrapped
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      // Otherwise return the inner data directly
      return innerData as RfqResponse;
    }
    // Fallback: if structure is different, return the data directly
    return response.data as any;
  }

  /**
   * Get RFQ list with pagination and filters
   */
  async getRfqList(params?: RfqListParams): Promise<RfqListResponse> {
    const response = await axiosClient.get<{
      success: boolean;
      data: RfqListResponse;
    }>(ENDPOINTS.RFQ.BASE, {
      params,
    });
    // Handle double-wrapped response: { success: true, data: { success: true, data: {...} } }
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      // Check if inner data is also wrapped
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      // Otherwise return the inner data directly
      return innerData as RfqListResponse;
    }
    // Fallback: if structure is different, return the data directly
    return response.data as any;
  }

  /**
   * Get a single RFQ by ID with quotes
   */
  async getRfqDetail(id: string): Promise<RfqDetailResponse> {
    const response = await axiosClient.get<{ success: boolean; data: any }>(
      ENDPOINTS.RFQ.byId(id)
    );

    // Handle double-wrapped response: { success: true, data: { success: true, data: {...} } }
    if (!response || !response.data) {
      return { data: undefined } as RfqDetailResponse;
    }

    let data: any = response.data;

    // Unwrap first level: response.data = { success: true, data: {...} }
    if (
      data &&
      typeof data === "object" &&
      "success" in data &&
      "data" in data
    ) {
      data = data.data;
    }

    // Unwrap second level if exists: data = { success: true, data: {...} }
    if (
      data &&
      typeof data === "object" &&
      "success" in data &&
      "data" in data
    ) {
      data = data.data;
    }

    // At this point, data should be the RFQ object with id, buyerId, etc.
    // Verify it's the RFQ object by checking for RFQ-specific properties
    if (
      data &&
      typeof data === "object" &&
      ("id" in data || "buyerId" in data || "title" in data)
    ) {
      // Return in RfqDetailResponse format: { data: RfqDetail }
      return { data: data as RfqDetail } as RfqDetailResponse;
    }

    // Fallback: try to return as-is (might already be in correct format)
    return { data: data as RfqDetail | undefined } as RfqDetailResponse;
  }

  /**
   * Cancel an RFQ
   */
  async cancelRfq(id: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosClient.delete<{
      success: boolean;
      message: string;
    }>(ENDPOINTS.RFQ.byId(id));
    return response.data;
  }

  /**
   * Submit a quote for an RFQ
   */
  async postQuote(
    rfqId: string,
    data: CreateQuotePayload
  ): Promise<QuoteResponse> {
    const response = await axiosClient.post<{
      success: boolean;
      data: QuoteResponse;
    }>(ENDPOINTS.RFQ.quotes(rfqId), data);
    // Handle double-wrapped response: { success: true, data: { success: true, data: {...} } }
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      // Check if inner data is also wrapped
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      // Otherwise return the inner data directly
      return innerData as QuoteResponse;
    }
    // Fallback: if structure is different, return the data directly
    return response.data as any;
  }

  /**
   * Update or withdraw a quote
   */
  async updateQuote(
    rfqId: string,
    quoteId: string,
    data: UpdateQuotePayload
  ): Promise<QuoteResponse> {
    const response = await axiosClient.patch<{
      success: boolean;
      data: QuoteResponse;
    }>(ENDPOINTS.RFQ.quoteById(rfqId, quoteId), data);
    // Handle double-wrapped response: { success: true, data: { success: true, data: {...} } }
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      // Check if inner data is also wrapped
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      // Otherwise return the inner data directly
      return innerData as QuoteResponse;
    }
    // Fallback: if structure is different, return the data directly
    return response.data as any;
  }

  /**
   * Accept a quote (buyer only)
   */
  async acceptQuote(
    rfqId: string,
    quoteId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await axiosClient.patch<{
      success: boolean;
      message: string;
    }>(ENDPOINTS.RFQ.acceptQuote(rfqId, quoteId));
    return response.data;
  }

  /**
   * Reject a quote
   */
  async rejectQuote(
    rfqId: string,
    quoteId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await axiosClient.patch<{
      success: boolean;
      message: string;
    }>(ENDPOINTS.RFQ.rejectQuote(rfqId, quoteId));
    return response.data;
  }

  /**
   * Get quotes for an RFQ
   */
  async getQuotesForRfq(
    rfqId: string,
    params?: PaginationParams
  ): Promise<QuoteListResponse> {
    const response = await axiosClient.get<{
      success: boolean;
      data: QuoteListResponse;
    }>(ENDPOINTS.RFQ.quotes(rfqId), {
      params,
    });
    // Handle double-wrapped response: { success: true, data: { success: true, data: {...} } }
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      // Check if inner data is also wrapped
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      // Otherwise return the inner data directly
      return innerData as QuoteListResponse;
    }
    // Fallback: if structure is different, return the data directly
    return response.data as any;
  }

  /**
   * Post a message in RFQ thread
   */
  async postMessage(
    rfqId: string,
    payload: { message: string; attachments?: string[] }
  ): Promise<{ id: string; message: string; createdAt: string }> {
    const response = await axiosClient.post<{
      success: boolean;
      data: { id: string; message: string; createdAt: string };
    }>(`${ENDPOINTS.RFQ.byId(rfqId)}/messages`, payload);
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Get messages for an RFQ
   */
  async getMessages(
    rfqId: string,
    params?: PaginationParams
  ): Promise<{
    items: any[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  }> {
    const response = await axiosClient.get<{ success: boolean; data: any }>(
      `${ENDPOINTS.RFQ.byId(rfqId)}/messages`,
      { params }
    );
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Mark message as read
   */
  async markMessageRead(
    rfqId: string,
    messageId: string
  ): Promise<{ success: boolean }> {
    const response = await axiosClient.patch<{ success: boolean }>(
      `${ENDPOINTS.RFQ.byId(rfqId)}/messages/${messageId}/read`
    );
    return response.data;
  }

  /**
   * Revise a quote
   */
  async reviseQuote(
    rfqId: string,
    quoteId: string,
    payload: {
      price: number;
      currency?: string;
      leadTimeDays: number;
      message?: string;
      attachments?: string[];
    }
  ): Promise<{ id: string; revisionIndex: number; createdAt: string }> {
    const response = await axiosClient.post<{
      success: boolean;
      data: { id: string; revisionIndex: number; createdAt: string };
    }>(`${ENDPOINTS.RFQ.byId(rfqId)}/quotes/${quoteId}/revise`, payload);
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Get quote revisions
   */
  async getQuoteRevisions(rfqId: string, quoteId: string): Promise<any[]> {
    const response = await axiosClient.get<{ success: boolean; data: any[] }>(
      `${ENDPOINTS.RFQ.byId(rfqId)}/quotes/${quoteId}/revisions`
    );
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Withdraw RFQ
   */
  async withdrawRfq(
    rfqId: string
  ): Promise<{ success: boolean; status: string }> {
    const response = await axiosClient.post<{
      success: boolean;
      data: { success: boolean; status: string };
    }>(`${ENDPOINTS.RFQ.byId(rfqId)}/withdraw`);
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Withdraw quote
   */
  async withdrawQuote(
    rfqId: string,
    quoteId: string
  ): Promise<{ success: boolean }> {
    const response = await axiosClient.post<{
      success: boolean;
      data: { success: boolean };
    }>(`${ENDPOINTS.RFQ.byId(rfqId)}/quotes/${quoteId}/withdraw`);
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Get RFQ activity log
   */
  async getRfqActivity(
    rfqId: string,
    params?: PaginationParams
  ): Promise<{
    items: any[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  }> {
    const response = await axiosClient.get<{ success: boolean; data: any }>(
      `${ENDPOINTS.RFQ.byId(rfqId)}/activity`,
      { params }
    );
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Create a dispute
   */
  async createDispute(
    rfqId: string,
    payload: { quoteId?: string; reason: string; description: string }
  ): Promise<{ id: string; status: string; createdAt: string }> {
    const response = await axiosClient.post<{
      success: boolean;
      data: { id: string; status: string; createdAt: string };
    }>(`${ENDPOINTS.RFQ.byId(rfqId)}/disputes`, payload);
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Get disputes for an RFQ
   */
  async getDisputes(rfqId: string): Promise<any[]> {
    const response = await axiosClient.get<{ success: boolean; data: any[] }>(
      `${ENDPOINTS.RFQ.byId(rfqId)}/disputes`
    );
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Resolve a dispute (admin only)
   */
  async resolveDispute(
    rfqId: string,
    disputeId: string,
    payload: { resolution: string; status: string }
  ): Promise<{ success: boolean; status: string }> {
    const response = await axiosClient.patch<{
      success: boolean;
      data: { success: boolean; status: string };
    }>(`${ENDPOINTS.RFQ.byId(rfqId)}/disputes/${disputeId}/resolve`, payload);
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Get RFQ metrics (admin only)
   */
  async getRfqMetrics(params?: {
    startDate?: string;
    endDate?: string;
    categoryId?: string;
    status?: string;
  }): Promise<{
    total: number;
    byStatus: Record<string, number>;
    avgQuotesPerRfq: number;
    avgTimeToFirstQuoteHours: number | null;
  }> {
    const response = await axiosClient.get<{ success: boolean; data: any }>(
      ENDPOINTS.RFQ.METRICS,
      { params }
    );
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Rate a quote (buyer only)
   */
  async rateQuote(
    rfqId: string,
    quoteId: string,
    rating: number
  ): Promise<{ success: boolean }> {
    const response = await axiosClient.patch<{
      success: boolean;
      data: { success: boolean };
    }>(
      `${ENDPOINTS.RFQ.byId(rfqId)}/quotes/${quoteId}/rate`,
      {},
      { params: { rating } }
    );
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * List all RFQs (admin only)
   */
  async getAdminRfqList(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    buyerId?: string;
    sellerId?: string;
    categoryId?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<RfqListResponse> {
    const response = await axiosClient.get<{
      success: boolean;
      data: RfqListResponse;
    }>(ENDPOINTS.RFQ.ADMIN_LIST, { params });
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Force close RFQ (admin only)
   */
  async forceCloseRfq(
    rfqId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await axiosClient.post<{
      success: boolean;
      message: string;
    }>(ENDPOINTS.RFQ.ADMIN_FORCE_CLOSE(rfqId));
    return response.data;
  }

  /**
   * Suspend/Unsuspend seller from quoting (admin only)
   */
  async suspendSellerFromQuoting(
    sellerId: string,
    suspend: boolean
  ): Promise<{ success: boolean; message: string; sellerStatus: string }> {
    const response = await axiosClient.post<{
      success: boolean;
      message: string;
      sellerStatus: string;
    }>(
      ENDPOINTS.RFQ.ADMIN_SUSPEND_SELLER(sellerId),
      {},
      { params: { suspend: suspend.toString() } }
    );
    return response.data;
  }

  /**
   * Get all quotes by seller (seller only)
   */
  async getSellerQuotes(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{
    items: Array<
      RfqQuote & { rfq?: { id: string; title: string; status: string } }
    >;
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  }> {
    const response = await axiosClient.get<{ success: boolean; data: any }>(
      ENDPOINTS.RFQ.SELLER_QUOTES,
      { params }
    );
    if (response.data && response.data.success) {
      const innerData = response.data.data;
      if (
        innerData &&
        typeof innerData === "object" &&
        "success" in innerData &&
        "data" in innerData
      ) {
        return (innerData as any).data;
      }
      return innerData;
    }
    return response.data as any;
  }

  /**
   * Export RFQs to CSV (admin only)
   */
  async exportRfqs(params?: {
    search?: string;
    status?: string;
    buyerId?: string;
    sellerId?: string;
    categoryId?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<Blob> {
    const response = await axiosClient.get(ENDPOINTS.RFQ.ADMIN_EXPORT, {
      params,
      responseType: "blob",
    });
    return response.data;
  }
}

export const rfqService = new RfqService();
export default rfqService;
